import { Box, chakra, HStack, Text, VStack } from '@chakra-ui/react'
import { useQueryClient } from '@tanstack/react-query'
import { memo, useCallback, useEffect, useId, useRef } from 'react'

import type { ToastLike } from '@mangovoice/mango-ui'

import type { HotelsApi } from '../../api/hotelsApi'
import { useHotelsI18n } from '../../i18n'
import { HOTELS_QUERY_KEYS } from '../../queries/queryKeys'
import { prefetchRoomWakeupData } from '../../queries/roomWakeupData'
import type { ApiError } from '../../types/api'
import type {
  EnrichedRoom,
  HospitalityPermissions,
  PaginatedResult,
} from '../../types/rooms'
import type { Voicemail } from '../../types/voicemail'
import { getRoomStatusConfig } from '../../utils/statusHelpers'
import type { WakeupNotificationTone } from '../../utils/wakeupDashboard'
import { getRoomStatusLabel } from '../rooms/roomStatusLabels'
import { VoicemailPopover } from './VoicemailPopover'
import { WakeUpCallPopover } from './WakeUpCallPopover'

const HOVER_INTENT_DELAY_MS = 140
const VOICEMAILS_PREFETCH_LIMIT = 4

interface RoomStatusTileProps {
  room: EnrichedRoom
  onClick: (room: EnrichedRoom) => void
  api: HotelsApi
  pbxId: number
  pbxUuid: string
  pbxTimeZone: string
  toast: ToastLike
  permissions: HospitalityPermissions
  wakeupTone?: WakeupNotificationTone
}

export const RoomStatusTile = memo(function RoomStatusTile({
  room,
  onClick,
  api,
  pbxId,
  pbxUuid,
  pbxTimeZone,
  toast,
  permissions,
  wakeupTone = null,
}: RoomStatusTileProps) {
  const { t, text } = useHotelsI18n()
  const queryClient = useQueryClient()
  const prefetchTimerRef = useRef<number | null>(null)
  const hasPrefetchedIntentRef = useRef(false)
  const config = getRoomStatusConfig(room.status_code)
  const statusLabel = getRoomStatusLabel(room.status_code, t) || config.label
  const descriptionIdPrefix = useId()
  const roomNameId = `${descriptionIdPrefix}-name`
  const statusId = `${descriptionIdPrefix}-status`
  const openRoomDescribedBy = room.name ? `${roomNameId} ${statusId}` : statusId

  const cancelPrefetchIntent = useCallback(() => {
    if (prefetchTimerRef.current === null) return
    window.clearTimeout(prefetchTimerRef.current)
    prefetchTimerRef.current = null
  }, [])

  const prefetchPopoverData = useCallback(() => {
    if (hasPrefetchedIntentRef.current) return

    hasPrefetchedIntentRef.current = true

    if (pbxUuid && (room.wakeup_calls_count ?? 0) > 0) {
      void prefetchRoomWakeupData({
        queryClient,
        api,
        pbxUuid,
        room,
        staleTime: 30_000,
      })
    }

    if (pbxId > 0 && room.id > 0 && (room.voicemail_count ?? 0) > 0) {
      void queryClient.prefetchQuery<PaginatedResult<Voicemail>, ApiError>({
        queryKey: HOTELS_QUERY_KEYS.voicemailsList(pbxId, room.id),
        queryFn: async () => {
          const response = await api.listVoicemails({
            room_id: room.id,
            pbx_id: pbxId,
            limit: VOICEMAILS_PREFETCH_LIMIT,
            ordering: '-created_epoch',
          })
          if (!response.ok) {
            return Promise.reject(response.data as ApiError)
          }
          return response.data as PaginatedResult<Voicemail>
        },
        staleTime: 30_000,
      })
    }
  }, [
    api,
    pbxId,
    pbxUuid,
    queryClient,
    room.id,
    room.uuid,
    room.voicemail_count,
    room.wakeup_calls_count,
  ])

  const schedulePrefetchIntent = useCallback(() => {
    if (hasPrefetchedIntentRef.current) return
    cancelPrefetchIntent()
    prefetchTimerRef.current = window.setTimeout(() => {
      prefetchTimerRef.current = null
      prefetchPopoverData()
    }, HOVER_INTENT_DELAY_MS)
  }, [cancelPrefetchIntent, prefetchPopoverData])

  useEffect(() => {
    hasPrefetchedIntentRef.current = false
    cancelPrefetchIntent()
  }, [cancelPrefetchIntent, room.id, room.uuid, pbxId, pbxUuid])

  useEffect(() => {
    return () => {
      cancelPrefetchIntent()
    }
  }, [cancelPrefetchIntent])

  return (
    <Box
      data-testid="room-status-tile"
      onMouseEnter={schedulePrefetchIntent}
      onMouseLeave={cancelPrefetchIntent}
      onFocus={schedulePrefetchIntent}
      onBlur={(e: React.FocusEvent<HTMLDivElement>) => {
        if (e.currentTarget.contains(e.relatedTarget)) return
        cancelPrefetchIntent()
      }}
      position="relative"
      p={3}
      borderWidth={2}
      borderColor={{ base: config.border, _dark: config.borderDark }}
      borderRadius="l2"
      bg={{ base: config.bg, _dark: config.bgDark }}
      _hover={{ opacity: 0.85 }}
      textAlign="left"
      w="full"
    >
      <chakra.button
        type="button"
        aria-label={text(
          'hotels.dashboard.roomTile.openAria',
          'Open room {{room}}',
          {
            room: room.number,
          },
        )}
        aria-describedby={openRoomDescribedBy}
        onClick={() => onClick(room)}
        position="absolute"
        inset={0}
        borderRadius="l2"
        cursor="pointer"
        focusVisibleRing="outside"
      />
      <VStack align="start" gap={1}>
        <HStack justify="space-between" w="full" align="center">
          <Text fontWeight="bold" fontSize="sm">
            {room.number}
          </Text>
          <Text
            id={statusId}
            fontSize="xs"
            fontWeight="semibold"
            color={{ base: config.text, _dark: config.textDark }}
          >
            {statusLabel}
          </Text>
        </HStack>
        <HStack justify="space-between" w="full">
          <Text id={roomNameId} fontSize="xs" color="fg.muted" truncate>
            {room.name || '\u00A0'}
          </Text>
          <HStack
            position="relative"
            zIndex={1}
            gap={2.5}
            flexShrink={0}
            bg="bg.subtle"
            borderRadius="lg"
            px={1.5}
            py={1.5}
          >
            <WakeUpCallPopover
              room={room}
              api={api}
              pbxUuid={pbxUuid}
              pbxTimeZone={pbxTimeZone}
              toast={toast}
              permissions={permissions}
              prefetchedTone={wakeupTone}
            />
            <VoicemailPopover
              room={room}
              api={api}
              pbxId={pbxId}
              toast={toast}
              permissions={permissions}
            />
          </HStack>
        </HStack>
      </VStack>
    </Box>
  )
})
